import { useLazyQuery } from "@apollo/client";
import { THEME } from "@constants/global";
import { css } from "@emotion/css";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import IaButton from "components/core/iaButton/IaButton";
import { ArrowRight, AtSign, Bold, Image as ImageIcon, Italic, Link, Paperclip, Search, Trash2, Underline, Unlink } from "lucide-react";
import {
  CanvaElement,
  FileElement,
  HOTKEYS,
  ImageElement,
  LinkElement,
  MentionElement,
  VideoElement
} from "components/Note/extras/custom-types";
import {
  IButtonProp,
  SlateButton,
  Toolbar,
  isImageUrl
} from "components/Note/extras/extras";
import GifPicker, { Theme } from "gif-picker-react";
import { gqlFeedback } from "gql";
import isHotkey, { isKeyHotkey } from "is-hotkey";
import isUrl from "is-url";
import { useTheme } from "next-themes";
import useTranslation from "next-translate/useTranslation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import { Checkbox } from "rsuite";
import {
  Descendant,
  Editor,
  Range,
  Element as SlateElement,
  Transforms,
  createEditor
} from "slate";
import { withHistory } from "slate-history";
import {
  Editable,
  ReactEditor,
  Slate,
  useFocused,
  useSelected,
  useSlate,
  useSlateStatic,
  withReact
} from "slate-react";
import { IUser } from "types/User.types";
import {
  collectClipboardImageFiles,
  uploadClipboardImageFile,
  uploadFileWithSignedUrl,
} from "utils/uploadClipboardImage";
import { cn } from "@/lib/utils";
import Leaf from "./components/Leaf";
import ImageNote from "./components/ImageNote/imageNote";
import FileNote from "./components/FileNote/fileNote";

const NOTE3_DOC_ACCEPT =
  "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";
const NOTE3_DOC_EXT = /\.(pdf|docx?|xlsx?|pptx?|txt|csv)$/i;

function isNote3UploadableFile(file: File): boolean {
  if ((file.type || "").startsWith("image/")) return true;
  if (NOTE3_DOC_EXT.test(file.name || "")) return true;
  const mime = file.type || "";
  return (
    mime === "application/pdf" ||
    mime === "text/plain" ||
    mime === "text/csv" ||
    mime.includes("msword") ||
    mime.includes("officedocument") ||
    mime.includes("ms-excel") ||
    mime.includes("ms-powerpoint")
  );
}

function insertImageNode(editor: Editor, url: string) {
  const text = { text: "" };
  const image: ImageElement = { type: "image", url, children: [text] };
  Transforms.insertNodes(editor, image);
}

function insertFileNode(
  editor: Editor,
  payload: { url: string; name?: string; mime?: string }
) {
  const text = { text: "" };
  const file: FileElement = {
    type: "file",
    url: payload.url,
    name: payload.name,
    mime: payload.mime,
    children: [text],
  };
  Transforms.insertNodes(editor, file);
}

function normalizeCanvaEmbedUrl(url: string): string {
  try {
    const canvaUrl = new URL(url);
    if (!canvaUrl.hostname.includes("canva.com")) return url;
    if (canvaUrl.searchParams.has("embed")) return canvaUrl.toString();

    return `${canvaUrl.toString()}${canvaUrl.search ? "&" : "?"}embed`;
  } catch {
    return url;
  }
}

function getCanvaEmbedUrl(data: DataTransfer): string | null {
  const html = data.getData("text/html");
  const plainText = data.getData("text/plain").trim();
  const source = html || plainText;

  if (source) {
    const tmp = document.createElement("div");
    tmp.innerHTML = source;
    const iframeSrc = tmp.querySelector("iframe")?.getAttribute("src")?.trim();
    if (iframeSrc?.includes("canva.com")) {
      return normalizeCanvaEmbedUrl(iframeSrc);
    }
  }

  if (plainText.includes("canva.com") && isUrl(plainText)) {
    return normalizeCanvaEmbedUrl(plainText);
  }

  return null;
}

function clipboardImageToastError(
  error: unknown,
  t: (key: string) => string
): string {
  if (error instanceof Error) {
    if (error.message === "IMAGE_TOO_LARGE") {
      return t("Imagen muy grande (máx. 10MB)");
    }
  }
  return t("Error al pegar imagen");
}

function FormatBtn({
  active,
  onMouseDown,
  children,
  className,
}: {
  active: boolean;
  onMouseDown: (event: React.MouseEvent) => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onMouseDown={onMouseDown}
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors",
        active
          ? "bg-slate-200/90 text-slate-900 dark:bg-zinc-700 dark:text-zinc-100"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100",
        className
      )}
    >
      {children}
    </button>
  );
}

interface IBaseProps {
  placeholder?: string;
  usersToMention?: IUser[];
  onChange?: (value: string) => void;
  initialValueString?: any;
  enableImageButton?: boolean;
  enableTextButtons?: boolean;
  enableLinkButton?: boolean;
  cleanEditor?: boolean;
  showIAButton?: boolean;
  compact?: boolean;
  centered?: boolean;
  /** Editor compacto para pegar embeds (p. ej. Canva). */
  embedComposer?: boolean;
  /** Notifica al padre cuando hay imágenes subiendo desde portapapeles. */
  onImageUploadChange?: (loading: boolean, pendingCount?: number) => void;
  /** Elimina el archivo del storage antes de quitar el nodo del editor. */
  onImageRemove?: (url: string) => Promise<void>;
  /** Ref para que el padre dispare la misma eliminación (p. ej. desde preview). */
  imageRemoveHandlerRef?: React.MutableRefObject<
    ((url: string) => Promise<void>) | null
  >;
}

export const Portal = ({ children }) =>
  typeof document === "object"
    ? ReactDOM.createPortal(children, document.body)
    : null;

export default function Note3(props: IBaseProps) {
  const {
    placeholder,
    usersToMention,
    onChange,
    initialValueString,
    enableImageButton = true,
    enableTextButtons = true,
    enableLinkButton = true,
    cleanEditor = false,
    showIAButton = true,
    compact = false,
    centered = false,
    embedComposer = false,
    onImageUploadChange,
    onImageRemove,
    imageRemoveHandlerRef,
  } = props;

  const { theme } = useTheme();
  const [isUrlInputOpen, setIsUrlInputOpen] = useState<boolean>(false);
  const [isGifOpen, setIsGifOpen] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<boolean>(true);
  const [selected, setSelected] = useState<string | null>(null);
  const { t } = useTranslation("common");

  const [signedUrl] = useLazyQuery(gqlFeedback.queries.GENERATE_SIGNED_URL, {
    fetchPolicy: "network-only"
  });
  const uploadContextRef = useRef({
    signedUrl,
    t,
  });
  uploadContextRef.current = { signedUrl, t };
  const onImageUploadChangeRef = useRef(onImageUploadChange);
  onImageUploadChangeRef.current = onImageUploadChange;
  const clipboardImageUploadLockRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadAndInsertClipboardImages = useCallback(
    async (data: DataTransfer, targetEditor: Editor) => {
      if (clipboardImageUploadLockRef.current) return false;

      const imageFiles = collectClipboardImageFiles(data);
      if (!imageFiles.length) return false;

      clipboardImageUploadLockRef.current = true;

      const { signedUrl: requestSignedUrl, t: translate } =
        uploadContextRef.current;

      const resolveSignedUrl = async (key: string) => {
        const result = await requestSignedUrl({ variables: { key } });
        const url = result.data?.getSignedUrl;
        if (!url) throw new Error("UPLOAD_FAILED");
        return url as string;
      };

      onImageUploadChangeRef.current?.(true, imageFiles.length);

      try {
        let remaining = imageFiles.length;

        for (const file of imageFiles) {
          const uploadedUrl = await toast.promise(
            uploadClipboardImageFile(file, resolveSignedUrl),
            {
              loading: translate("Pegando imagen"),
              success: translate("Imagen pegada con éxito"),
              error: (error) => clipboardImageToastError(error, translate),
            }
          );
          insertImageNode(targetEditor, uploadedUrl as string);
          remaining -= 1;
          onImageUploadChangeRef.current?.(remaining > 0, remaining);
        }
      } finally {
        onImageUploadChangeRef.current?.(false, 0);
        clipboardImageUploadLockRef.current = false;
      }

      return true;
    },
    []
  );

  const uploadAndInsertFiles = useCallback(
    async (files: File[], targetEditor: Editor) => {
      if (clipboardImageUploadLockRef.current) return false;

      const uploadableFiles = files.filter(isNote3UploadableFile);
      if (!uploadableFiles.length) return false;

      clipboardImageUploadLockRef.current = true;

      const { signedUrl: requestSignedUrl, t: translate } =
        uploadContextRef.current;

      const resolveSignedUrl = async (key: string) => {
        const result = await requestSignedUrl({ variables: { key } });
        const url = result.data?.getSignedUrl;
        if (!url) throw new Error("UPLOAD_FAILED");
        return url as string;
      };

      onImageUploadChangeRef.current?.(true, uploadableFiles.length);

      try {
        let remaining = uploadableFiles.length;

        for (const file of uploadableFiles) {
          const isImage = (file.type || "").startsWith("image/");
          const uploadedUrl = await toast.promise(
            isImage
              ? uploadClipboardImageFile(file, resolveSignedUrl)
              : uploadFileWithSignedUrl(file, resolveSignedUrl),
            {
              loading: isImage
                ? translate("Pegando imagen")
                : translate("Subiendo archivo"),
              success: isImage
                ? translate("Imagen pegada con éxito")
                : translate("Archivo subido con éxito"),
              error: (error) => clipboardImageToastError(error, translate),
            }
          );

          if (isImage) {
            insertImageNode(targetEditor, uploadedUrl as string);
          } else {
            insertFileNode(targetEditor, {
              url: uploadedUrl as string,
              name: file.name,
              mime: file.type || undefined,
            });
          }

          remaining -= 1;
          onImageUploadChangeRef.current?.(remaining > 0, remaining);
        }
      } finally {
        onImageUploadChangeRef.current?.(false, 0);
        clipboardImageUploadLockRef.current = false;
      }

      return true;
    },
    []
  );

  const withMentions = (editor) => {
    const { isInline, isVoid } = editor;

    editor.isInline = (element) =>
      element?.type === "mention" ? true : isInline(element);

    editor.isVoid = (element) =>
      element?.type === "mention" ? true : isVoid(element);

    return editor;
  };

  const withImages = (editor) => {
    const { insertData, isVoid } = editor;

    editor.isVoid = (element) =>
      element.type === "image" || element.type === "file"
        ? true
        : isVoid(element);

    editor.insertData = async (data) => {
      const text = data.getData("text/plain");
      const handled = await uploadAndInsertClipboardImages(data, editor);
      if (handled) return;

      if (isImageUrl(text)) {
        insertImageNode(editor, text);
      } else {
        insertData(data);
      }
    };

    return editor;
  };

  const withEmbeds = (editor) => {
    const { insertData } = editor;
    editor.insertData = (data) => {
      const html = data.getData("text/html");
      const text = data.getData("text/plain").trim();
      const source = html || text;
      const tmp = document.createElement("div");
      tmp.innerHTML = source;
      const src = tmp.querySelector("iframe")?.getAttribute("src")?.trim();
      const canvaUrl = getCanvaEmbedUrl(data);

      if (canvaUrl) {
        insertCanvaEmbed(editor, canvaUrl);
      } else if (src && isUrl(src)) {
        insertVideo(editor, src);
      } else {
        insertData(data);
      }
    };

    return editor;
  };

  const insertMention = (editor, character: IUser) => {
    const mention: MentionElement = {
      type: "mention",
      character: `${character.name} ${character.lastname || ""}`,
      id: character.id,
      children: [{ text: "" }]
    };
    Transforms.insertNodes(editor, mention);
    Transforms.move(editor);
  };

  const UrlInput = ({ url, onChange }) => {
    const [value, setValue] = React.useState(url);
    return (
      <input
        value={value}
        onClick={(e) => e.stopPropagation()}
        className="mt-2 w-full rounded-md border-2 p-1 text-xs"
        onChange={(e) => {
          const newUrl = e.target.value;
          setValue(newUrl);
          onChange(newUrl);
        }}
      />
    );
  };

  const Element = (props) => {
    const { attributes, children, element } = props;
    const style = { textAlign: element.align };

    switch (element.type) {
      case "image":
        return <ImageNote {...props} />;
      case "file":
        return <FileNote {...props} />;
      case "video":
        return <VideoElement {...props} />;
      case "canva":
        return <CanvaElement {...props} />;
      case "mention":
        return <Mention {...props} />;
      case "block-quote":
        return (
          <blockquote style={style} {...attributes}>
            {children}
          </blockquote>
        );
      case "bulleted-list":
        return (
          <ul style={style} {...attributes}>
            {children}
          </ul>
        );
      case "heading-one":
        return (
          <h1 style={style} {...attributes}>
            {children}
          </h1>
        );
      case "heading-two":
        return (
          <h2 style={style} {...attributes}>
            {children}
          </h2>
        );
      case "list-item":
        return (
          <li style={style} {...attributes}>
            {children}
          </li>
        );
      case "numbered-list":
        return (
          <ol style={style} {...attributes}>
            {children}
          </ol>
        );
      case "link":
        return <LinkComponent {...props} />;
      default:
        return <p {...attributes}>{children}</p>;
    }
  };

  const InlineChromiumBugfix = () => (
    <span
      contentEditable={false}
      className={css`
        font-size: 0;
      `}
    >
      ${String.fromCodePoint(160) /* Non-breaking space */}
    </span>
  );

  const LinkComponent = ({ attributes, children, element }) => {
    const selected = useSelected();
    return (
      <a
        {...attributes}
        href={element.url}
        className={selected ? "border-2 border-gray-500" : ""}
      >
        <InlineChromiumBugfix />
        {children}
        <InlineChromiumBugfix />
      </a>
    );
  };

  const Mention = ({ attributes, children, element }) => {
    const selected = useSelected();
    const focused = useFocused();

    return (
      <span
        {...attributes}
        contentEditable={false}
        data-cy={`mention-${element.character.replace(" ", "-")}`}
        style={{
          padding: "3px 3px 2px",
          margin: "1px 1px",
          verticalAlign: "baseline",
          display: "inline-block",
          borderRadius: "4px",
          backgroundColor: `${theme === THEME.dark ? "#333" : "#eee"}`,
          fontSize: "0.9em",
          boxShadow:
            selected && focused
              ? `0 0 0 2px ${theme === THEME.dark ? "#333" : "#B4D5FF"}`
              : "none"
        }}
      >
        @{element.character}
        {children}
      </span>
    );
  };

  const InsertGifButton = () => {
    const onMouseDown = (event: React.MouseEvent) => {
      event.preventDefault();
      setSelected("gif");
      setIsGifOpen(true);
    };

    if (compact) {
      return (
        <FormatBtn active={false} onMouseDown={onMouseDown}>
          <ImageIcon className="h-4 w-4" />
        </FormatBtn>
      );
    }

    return (
      <SlateButton active={false} onMouseDown={onMouseDown}>
        <ImageIcon className="h-4 w-4" />
      </SlateButton>
    );
  };

  const InsertFileButton = () => {
    const onMouseDown = (event: React.MouseEvent) => {
      event.preventDefault();
      fileInputRef.current?.click();
    };

    if (compact) {
      return (
        <FormatBtn active={false} onMouseDown={onMouseDown}>
          <Paperclip className="h-4 w-4" />
        </FormatBtn>
      );
    }

    return (
      <SlateButton active={false} onMouseDown={onMouseDown}>
        <Paperclip className="h-4 w-4" />
      </SlateButton>
    );
  };

  const handleTooglePreview = () => {
    setPreviewUrl(!previewUrl);
  };

  function VideoElement({ attributes, children, element }) {
    const editor = useSlateStatic();
    const { url } = element;

    // * check if url is loom
    const isLoom = /https?:\/\/www\.loom\.com\/embed\/[a-zA-Z0-9]+/.test(url);
    let extraQueryParams = "";

    if (isLoom) {
      extraQueryParams =
        "&hide_share=true&hideEmbedTopBar=true&hide_title=true&hide_owner=true&t=0s&muted=false&autoplay=false";
    }

    return (
      <div {...attributes}>
        <div contentEditable={false}>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "400px"
            }}
          >
            <iframe
              title="video"
              src={`${url}?&byline=0&portrait=0${isLoom ? extraQueryParams : ""}`}
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "400px",
                borderRadius: "16px"
              }}
            />
            <button
              onClick={() =>
                Transforms.removeNodes(editor, {
                  at: ReactEditor.findPath(editor, element)
                })
              }
              className="absolute right-2 top-2 rounded-full border border-white bg-white px-3 py-2 text-white"
            >
              <Trash2 className="h-4 w-4 text-gray-900" />
            </button>
          </div>

          {/* remove element using button */}
        </div>
        {children}
      </div>
    );
  }

  const CanvaElement = ({ attributes, children, element }) => {
    const editor = useSlateStatic();
    const { url } = element;
    return (
      <div {...attributes}>
        <div contentEditable={false}>
          <div
            className={cn(
              "relative aspect-video w-full overflow-hidden rounded-xl bg-slate-950",
              !embedComposer && "min-h-[260px]"
            )}
          >
            <iframe
              title="Canva"
              src={`${url}`}
              loading="lazy"
              allow="fullscreen"
              allowFullScreen
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
          <UrlInput
            url={url}
            onChange={(val) => {
              const path = ReactEditor.findPath(editor, element);
              const newProperties: Partial<SlateElement> = {
                url: normalizeCanvaEmbedUrl(val)
              };
              Transforms.setNodes<SlateElement>(editor, newProperties, {
                at: path
              });
            }}
          />
        </div>
        {children}
      </div>
    );
  };

  const insertVideo = (editor, url) => {
    const text = { text: "" };
    const video: VideoElement = { type: "video", url, children: [text] };
    Transforms.insertNodes(editor, video);
  };

  const insertCanvaEmbed = (editor, url) => {
    const text = { text: "" };
    const canva: CanvaElement = {
      type: "canva",
      url: normalizeCanvaEmbedUrl(url),
      children: [text]
    };
    Transforms.insertNodes(editor, canva);
  };

  const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  };

  const isLinkActive = (editor) => {
    const [link] = Editor.nodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link"
    });
    return !!link;
  };

  const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);

    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const MarkButton = ({ format, icon: MarkIcon }: IButtonProp) => {
    const editor = useSlate();
    const active = isMarkActive(editor, format);
    const onMouseDown = (event: React.MouseEvent) => {
      event.preventDefault();
      toggleMark(editor, format);
    };

    if (compact) {
      return (
        <FormatBtn active={active} onMouseDown={onMouseDown}>
          <MarkIcon className="h-3 w-3" />
        </FormatBtn>
      );
    }

    return (
      <SlateButton active={active} onMouseDown={onMouseDown}>
        <MarkIcon className="h-4 w-4" />
      </SlateButton>
    );
  };

  const unwrapLink = (editor) => {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link"
    });
  };

  const wrapLink = (editor, url: string) => {
    if (isLinkActive(editor)) {
      unwrapLink(editor);
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link: LinkElement = {
      type: "link",
      url,
      children: isCollapsed ? [{ text: url }] : [],
      preview: previewUrl
    };

    if (isCollapsed) {
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      Transforms.collapse(editor, { edge: "end" });
    }
  };

  const AddLinkButton = () => {
    const editor = useSlate();
    const active = isLinkActive(editor);
    const onMouseDown = (event: React.MouseEvent) => {
      event.preventDefault();
      setSelected("link");
      setIsUrlInputOpen(true);
    };

    if (compact) {
      return (
        <FormatBtn active={active} onMouseDown={onMouseDown}>
          <Link className="h-4 w-4" />
        </FormatBtn>
      );
    }

    return (
      <SlateButton active={active} onMouseDown={onMouseDown}>
        <Link className="h-4 w-4" />
      </SlateButton>
    );
  };

  const RemoveLinkButton = () => {
    const editor = useSlate();
    const active = isLinkActive(editor);
    const onMouseDown = (event: React.MouseEvent) => {
      event.preventDefault();
      if (isLinkActive(editor)) {
        unwrapLink(editor);
      }
    };

    if (compact) {
      return (
        <FormatBtn active={active} onMouseDown={onMouseDown}>
          <Unlink className="h-4 w-4" />
        </FormatBtn>
      );
    }

    return (
      <SlateButton active={active} onMouseDown={onMouseDown}>
        <Unlink className="h-4 w-4" />
      </SlateButton>
    );
  };

  const initialValue: Descendant[] = initialValueString
    ? JSON.parse(initialValueString)
    : [
        {
          type: "paragraph",
          children: [
            {
              text: ""
            }
          ]
        }
      ];

  const withInlines = (editor) => {
    const { insertData, insertText, isInline } = editor;

    editor.isInline = (element) =>
      ["link", "button"].includes(element.type) || isInline(element);

    editor.insertText = (text) => {
      if (text && isUrl(text)) {
        wrapLink(editor, text);
      } else {
        insertText(text);
      }
    };

    editor.insertData = (data) => {
      const text = data.getData("text/plain");

      if (text && isUrl(text)) {
        wrapLink(editor, text);
      } else {
        insertData(data);
      }
    };

    return editor;
  };

  // * capitalize all users
  const capitelizedUsersToMention = usersToMention?.map((user) => ({
    ...user,
    name: user.name.charAt(0).toUpperCase() + user.name.slice(1),
    lastname: user.lastname?.charAt(0).toUpperCase() + user.lastname?.slice(1)
  }));

  const CHARACTERS = [...(capitelizedUsersToMention || [])];

  const ref = useRef<HTMLDivElement | null>(null);
  const [target, setTarget] = useState<Range | undefined | null>();
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState("");

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(
    () =>
      withImages(
        withEmbeds(
          withMentions(withReact(withInlines(withHistory(createEditor()))))
        )
      ),
    []
  );

  const removeImageByUrl = useCallback(
    async (url: string) => {
      const imageEntries = [
        ...Editor.nodes(editor, {
          at: [],
          match: (node) =>
            !Editor.isEditor(node) &&
            SlateElement.isElement(node) &&
            node.type === "image" &&
            node.url === url,
        }),
      ];

      if (!imageEntries.length) return;

      for (const [, path] of imageEntries.reverse()) {
        Transforms.removeNodes(editor, { at: path });
      }

      if (onImageRemove) {
        void onImageRemove(url);
      }

      toast.success(t("Imagen eliminada"));
    },
    [editor, onImageRemove, t]
  );

  React.useEffect(() => {
    if (!imageRemoveHandlerRef) return;
    imageRemoveHandlerRef.current = removeImageByUrl;
    return () => {
      imageRemoveHandlerRef.current = null;
    };
  }, [imageRemoveHandlerRef, removeImageByUrl]);

  const handleEditorPaste = useCallback(
    async (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (!event.clipboardData) return;

      const hasImages =
        collectClipboardImageFiles(event.clipboardData).length > 0;
      if (!hasImages) return;

      event.preventDefault();
      event.stopPropagation();
      await uploadAndInsertClipboardImages(event.clipboardData, editor);
    },
    [editor, uploadAndInsertClipboardImages]
  );

  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      event.target.value = "";
      if (!files.length) return;
      await uploadAndInsertFiles(files, editor);
    },
    [editor, uploadAndInsertFiles]
  );

  // Debounced search for better performance
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 150); // 150ms debounce

    return () => clearTimeout(timer);
  }, [search]);

  const chars = useMemo(() => {
    if (!debouncedSearch) return CHARACTERS.slice(0, 10);

    const searchTerm = debouncedSearch.toLowerCase();

    return CHARACTERS.filter((c: IUser) => {
      const fullName = `${c.name} ${c.lastname || ""}`.toLowerCase();
      const email = c.email?.toLowerCase() || "";

      // Exact match from start (highest priority)
      if (fullName.startsWith(searchTerm)) return true;

      // Email match
      if (email.includes(searchTerm)) return true;

      // Match from start of any word
      const words = fullName.split(" ");
      if (words.some((word) => word.startsWith(searchTerm))) return true;

      // Fuzzy match - check if all characters in search appear in order
      let searchIndex = 0;
      for (
        let i = 0;
        i < fullName.length && searchIndex < searchTerm.length;
        i++
      ) {
        if (fullName[i] === searchTerm[searchIndex]) {
          searchIndex++;
        }
      }
      return searchIndex === searchTerm.length;
    })
      .sort((a, b) => {
        const aName = `${a.name} ${a.lastname || ""}`.toLowerCase();
        const bName = `${b.name} ${b.lastname || ""}`.toLowerCase();

        // Prioritize exact matches at start
        const aStartsWithSearch = aName.startsWith(searchTerm);
        const bStartsWithSearch = bName.startsWith(searchTerm);

        if (aStartsWithSearch && !bStartsWithSearch) return -1;
        if (!aStartsWithSearch && bStartsWithSearch) return 1;

        // Then alphabetical
        return aName.localeCompare(bName);
      })
      .slice(0, 10); // Limit results
  }, [CHARACTERS, debouncedSearch]);

  const onKeyDown = useCallback(
    (event) => {
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        const { nativeEvent } = event;
        if (isKeyHotkey("left", nativeEvent)) {
          event.preventDefault();
          Transforms.move(editor, { unit: "offset", reverse: true });
          return;
        }
        if (isKeyHotkey("right", nativeEvent)) {
          event.preventDefault();
          Transforms.move(editor, { unit: "offset" });
          return;
        }
      }

      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event as any)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
        }
      }

      if (target) {
        switch (event.key) {
          case "ArrowDown": {
            event.preventDefault();
            const prevIndex = index >= chars.length - 1 ? 0 : index + 1;
            setIndex(prevIndex);
            // Scroll into view if needed
            const mentionEl = document.querySelector(
              `[data-mention-index="${prevIndex}"]`
            );
            if (mentionEl) {
              mentionEl.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
              });
            }
            break;
          }
          case "ArrowUp": {
            event.preventDefault();
            const nextIndex = index <= 0 ? chars.length - 1 : index - 1;
            setIndex(nextIndex);
            // Scroll into view if needed
            const mentionEl = document.querySelector(
              `[data-mention-index="${nextIndex}"]`
            );
            if (mentionEl) {
              mentionEl.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
              });
            }
            break;
          }
          case "Tab":
          case "Enter":
            event.preventDefault();
            if (target && chars.length > 0) {
              Transforms.select(editor, target);
              insertMention(editor, chars[index]);
              setTarget(null);
              setIndex(0);
              setSearch("");
            }
            break;
          case "Escape":
            event.preventDefault();
            setTarget(null);
            setIndex(0);
            setSearch("");
            break;
          case "mod+b":
            event.preventDefault();
            toggleMark(editor, "bold");
            break;
          case "mod+i":
            event.preventDefault();
            toggleMark(editor, "italic");
            break;
          case "mod+u":
            event.preventDefault();
            toggleMark(editor, "underline");
            break;
          default:
            break;
        }
      }
    },
    [index, search, target]
  );

  // Helper function to highlight search matches
  const highlightMatch = (text: string, search: string) => {
    if (!search) return text;

    const searchTerm = search.toLowerCase();
    const textLower = text.toLowerCase();
    const matchIndex = textLower.indexOf(searchTerm);

    if (matchIndex === -1) return text;

    return (
      <>
        {text.substring(0, matchIndex)}
        <mark className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-800">
          {text.substring(matchIndex, matchIndex + search.length)}
        </mark>
        {text.substring(matchIndex + search.length)}
      </>
    );
  };

  const handleClick = (selectedUser?: IUser) => {
    if (target) {
      Transforms.select(editor, target);
    }
    const userToInsert = selectedUser || chars[index];
    insertMention(editor, userToInsert);
    setTarget(null);
    setIndex(0);
    setSearch("");
    ReactEditor.focus(editor);
  };

  useEffect(() => {
    if (target && chars.length > 0) {
      const el = ref.current;
      if (el) {
        const domRange = ReactEditor.toDOMRange(editor, target);
        const rect = domRange.getBoundingClientRect();

        // Calculate optimal position
        const dropdownHeight = Math.min(chars.length * 60 + 100, 400); // Estimate dropdown height
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top = rect.top + window.pageYOffset + 24;
        let left = rect.left + window.pageXOffset;

        // Position above if not enough space below
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          top = rect.top + window.pageYOffset - dropdownHeight - 8;
        }

        // Adjust horizontal position if going off-screen
        const dropdownWidth = 400;
        if (left + dropdownWidth > window.innerWidth) {
          left = window.innerWidth - dropdownWidth - 16;
        }

        // Ensure minimum left position
        left = Math.max(16, left);

        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
      }
    }
  }, [chars.length, editor, index, search, target]);

  useEffect(() => {
    if (editor && editor.children.length > 0) {
      ReactEditor.focus(editor);
    }
  }, [editor]);

  // * hack for now
  useEffect(() => {
    // Ensure the editor has children nodes to work with
    if (editor && editor.children.length > 0) {
      // Safely delete content if it exists
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, [])
        }
      });

      // Remove the first node if it exists
      Transforms.removeNodes(editor, {
        at: [0]
      });
    }

    // Insert new initial value
    Transforms.insertNodes(editor, initialValue);

    // Focus on the editor
    ReactEditor.focus(editor);
  }, [cleanEditor]);

  const { lang } = useTranslation("common");

  return (
    <Slate
      editor={editor}
      value={initialValue}
      onChange={(value) => {
        const { selection } = editor;
        if (onChange) {
          onChange(JSON.stringify(value));
        }

        if (selection && Range.isCollapsed(selection)) {
          const [start] = Range.edges(selection);
          const wordBefore = Editor.before(editor, start, { unit: "word" });
          const before = wordBefore && Editor.before(editor, wordBefore);
          const beforeRange = before && Editor.range(editor, before, start);
          const beforeText = beforeRange && Editor.string(editor, beforeRange);
          const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);
          const after = Editor.after(editor, start);
          const afterRange = Editor.range(editor, start, after);
          const afterText = Editor.string(editor, afterRange);
          const afterMatch = afterText.match(/^(\s|$)/);

          if (beforeMatch && afterMatch) {
            setTarget(beforeRange);
            setSearch(beforeMatch[1]);
            setIndex(0);
            return;
          }
        }

        setTarget(null);
      }}
    >
      <div className="relative w-full">
        {showIAButton && (
          <div
            className={cn(
              "absolute z-30",
              compact ? "right-2 top-2" : "right-1 top-1"
            )}
          >
            <IaButton
              compact={compact}
              textToTransform={JSON.stringify(editor.children)}
              type="slate"
              setTextToTransform={
                onChange
                  ? (value) => {
                      onChange(value);

                      if (editor && editor.children.length > 0) {
                        Transforms.delete(editor, {
                          at: {
                            anchor: Editor.start(editor, []),
                            focus: Editor.end(editor, []),
                          },
                        });

                        Transforms.removeNodes(editor, {
                          at: [0],
                        });
                      }

                      Transforms.insertNodes(editor, JSON.parse(value));
                      ReactEditor.focus(editor);
                    }
                  : () => {}
              }
            />
          </div>
        )}

      <Toolbar
        className={cn(
          compact && "!mb-1.5 !pb-0 [&>*+*]:!ml-0.5",
          centered && "flex flex-wrap justify-center gap-1 [&>*+*]:!ml-0",
          showIAButton && "pr-9"
        )}
      >
        {enableTextButtons && (
          <>
            <MarkButton format="bold" icon={Bold} />
            <MarkButton format="italic" icon={Italic} />
            <MarkButton format="underline" icon={Underline} />
          </>
        )}
        {enableLinkButton && (
          <>
            <AddLinkButton />
            <RemoveLinkButton />
          </>
        )}
        {enableImageButton && (
          <>
            <InsertGifButton />
            <InsertFileButton />
          </>
        )}
      </Toolbar>
      {enableImageButton && (
        <input
          ref={fileInputRef}
          type="file"
          accept={NOTE3_DOC_ACCEPT}
          multiple
          hidden
          onChange={handleFileInputChange}
        />
      )}
      {isUrlInputOpen && (
        <>
          {selected === "link" && (
            <div className="flex items-center justify-between align-middle">
              <span className="text-xs">
                {t("Agrega una url de un enlace")}
              </span>
              <Checkbox
                className="text-xs"
                defaultChecked
                onChange={handleTooglePreview}
              >
                {" "}
                Preview
              </Checkbox>
            </div>
          )}
        </>
      )}

      {isGifOpen && (
        <div>
          <GifPicker
            onGifClick={(gif) => {
              insertImageNode(editor, gif.url);
              setIsGifOpen(false);
            }}
            tenorApiKey="AIzaSyASF8EDm-_cAK1xVVvBf6qTqey_tEdi9to"
            theme={theme === THEME.dark ? Theme.DARK : Theme.LIGHT}
            locale={lang}
          />
        </div>
      )}

      {!isUrlInputOpen && (
        <Editable
          renderElement={renderElement}
          onKeyDown={onKeyDown}
          onPaste={handleEditorPaste}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          className={cn(
            compact &&
              "pt-0.5 text-[13px] leading-snug text-slate-800 dark:text-zinc-200 [&_[data-slate-editor]]:!align-top [&_[data-slate-placeholder]]:!top-0 [&_[data-slate-placeholder]]:!translate-none [&_[data-slate-placeholder]]:!opacity-100 [&_[data-slate-placeholder]]:!text-slate-500 dark:[&_[data-slate-placeholder]]:!text-zinc-500",
            compact && !embedComposer && "min-h-[3.25rem]",
            embedComposer &&
              "min-h-0 [&_[data-slate-editor]]:!min-h-[2.75rem] [&_[data-slate-editor]]:py-1",
            centered &&
              "text-center [&_[data-slate-editor]]:!text-center [&_[data-slate-placeholder]]:!left-0 [&_[data-slate-placeholder]]:!right-0 [&_[data-slate-placeholder]]:!w-full [&_[data-slate-placeholder]]:!text-center",
            showIAButton && "pr-9"
          )}
        />
      )}
      </div>
      {target && chars.length > 0 && (
        <Portal>
          <div
            ref={ref}
            style={{
              top: "-9999px",
              left: "-9999px",
              position: "absolute",
              zIndex: 50,
              minWidth: "280px",
              maxWidth: "400px"
            }}
            className="mentions-portal animate__animated animate__fadeIn"
            data-cy="mentions-portal"
          >
            {/* Enhanced dropdown container */}
            <div
              className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800 ${theme === THEME.dark ? "bg-opacity-95" : "bg-opacity-98"} `}
            >
              {/* Header */}
              <div className="dark:bg-gray-900/50 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <AtSign className="h-3.5 w-3.5 text-sm text-blue-500" />
                  <span className="text-sm font-medium text-slate-700 dark:text-zinc-300">
                    {t("Mencionar usuario")}
                  </span>
                  {search && (
                    <span className="text-xs text-slate-500 dark:text-zinc-400">
                      • {chars.length}{" "}
                      {chars.length === 1 ? t("resultado") : t("resultados")}
                    </span>
                  )}
                </div>
              </div>

              {/* Users list */}
              <div className="max-h-64 overflow-y-auto">
                {chars.length > 0 ? (
                  chars.map((char, i) => (
                    <div
                      onClick={() => handleClick(char)}
                      key={char.id}
                      data-mention-index={i}
                      className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-all duration-200 ${
                        i === index
                          ? "border-r-2 border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                          : "dark:hover:bg-gray-700/50 hover:bg-gray-50"
                      } ${i !== chars.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""} `}
                      onMouseEnter={() => setIndex(i)}
                    >
                      {/* User Avatar */}
                      <div className="flex-shrink-0">
                        <AvatarWithInitials
                          customSize={32}
                          urlImg={char.profileImage}
                          initials={`${char.name} ${char.lastname || ""}`}
                        />
                      </div>

                      {/* User Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`truncate text-sm font-medium ${
                              i === index
                                ? "text-blue-700 dark:text-blue-300"
                                : "text-gray-900 dark:text-gray-100"
                            } `}
                          >
                            {highlightMatch(
                              `${char.name} ${char.lastname || ""}`,
                              search
                            )}
                          </span>

                          {/* Role badge if available */}
                          {char.role?.name && (
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                i === index
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200"
                                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              } `}
                            >
                              {char.role.name}
                            </span>
                          )}
                        </div>

                        {/* Email or additional info */}
                        {char.email && (
                          <p
                            className={`mt-0.5 truncate text-xs ${
                              i === index
                                ? "text-blue-600 dark:text-blue-400"
                                : "text-slate-500 dark:text-zinc-400"
                            } `}
                          >
                            {char.email}
                          </p>
                        )}
                      </div>

                      {/* Selection indicator */}
                      {i === index && (
                        <div className="flex-shrink-0">
                          <ArrowRight className="h-3.5 w-3.5 animate-pulse text-sm text-blue-500" />
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  // Empty state
                  <div className="px-4 py-6 text-center">
                    <Search className="h-8 w-8 mx-auto mb-2 block text-2xl text-gray-400" />
                    <p className="mb-1 text-sm text-slate-500 dark:text-zinc-400">
                      {t("No se encontraron usuarios")}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {search
                        ? t(`Intenta con "${search}"`)
                        : t("Escribe para buscar")}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer with keyboard shortcuts */}
              <div className="dark:bg-gray-900/50 border-t border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700">
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <kbd className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        ↑↓
                      </kbd>
                      {t("navegar")}
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        Enter
                      </kbd>
                      {t("seleccionar")}
                    </span>
                  </div>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                      Esc
                    </kbd>
                    {t("cancelar")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </Slate>
  );
}
